import { RiLoader3Line, RiResetRightLine } from '@remixicon/react'
import { useEffect, useState } from 'react'

import { useFlows } from '@/api/flows/flows'
import type { Detail } from '@/api/orders/orders-types'
import { useStageMutation } from '@/api/production-logs/production-logs'
import { isCompletedDetailStatus } from '@/components/common/selectors/detail-status-selector/config/statuses'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getAllProdLogsDone, getFlowInfo } from '@/utils/details'

export const DetailStageSelector = ({ detail }: { detail: Detail }) => {
  const { data: flows } = useFlows()

  const { currentFlow, currentFlows } = getFlowInfo({ detail, flows: flows?.results || [] })

  const currentLog = detail?.production_logs?.at(-1)
  const [stage, setStage] = useState(
    currentFlow?.stages.find((stage) => stage.id === currentLog?.stage_id)?.id?.toString() || ''
  )
  const [open, setOpen] = useState(false)

  const currentFlowStages = currentFlow?.stages

  const { handleStageMutation, handleStageReset, isPending } = useStageMutation()

  const handleStageChange = (stageId: string) => {
    if (currentFlow?.inven) {
      setStage(stageId)
      handleStageMutation({
        stage_id: +stageId,
        flow_inven: currentFlow.inven,
        detail,
        production_date: currentLog?.production_date ? currentLog.production_date : undefined
      })
    }
  }

  useEffect(() => {
    setStage(currentFlow?.stages.find((stage) => stage.id === currentLog?.stage_id)?.id?.toString() || '')
  }, [currentFlow?.stages, currentLog?.stage_id])

  const allLogsDone = getAllProdLogsDone({ logs: detail?.production_logs ?? [], flows: currentFlows })

  const selectorDisabled =
    currentFlowStages?.length === 0 ||
    !Object.keys(detail.standard_times ?? {}).length ||
    detail?.item?.status === 'ready_for_pack' ||
    isCompletedDetailStatus(detail?.item?.status) ||
    allLogsDone ||
    !!detail.item?.is_blocked

  const resetDisabled =
    selectorDisabled ||
    !currentLog ||
    (!currentLog?.stage_id && !currentLog?.start_time) ||
    isPending

  const onReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    handleStageReset(detail)
    setStage('')
    setOpen(false)
  }

  return (
    <div className='flex min-w-0 flex-1 items-center gap-2'>
      <Select
        key={stage}
        disabled={selectorDisabled}
        value={stage}
        open={open}
        onOpenChange={setOpen}
        onValueChange={handleStageChange}
      >
        <SelectTrigger className='h-8 min-w-0 flex-1'>
          <SelectValue placeholder='Choose machine' />
        </SelectTrigger>
        <SelectContent align='end'>
          {currentFlowStages?.map((stage) => {
            return (
              <SelectItem
                key={stage.id}
                value={stage.id?.toString()}
              >
                {stage.name}
              </SelectItem>
            )
          })}
          <footer
            className='border-t border-grey-200 p-1.5'
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant='ghost'
              size='sm'
              className='w-full justify-center gap-1.5 bg-grey-100 hover:bg-red-300 hover:text-white-100 hover:[&_svg]:text-white-100'
              onClick={onReset}
              disabled={resetDisabled}
              aria-label='Reset stage'
            >
              {isPending ? (
                <RiLoader3Line className='size-3.5 animate-spin' />
              ) : (
                <RiResetRightLine className='size-3.5' />
              )}
              <span className='text-xs'>Reset</span>
            </Button>
          </footer>
        </SelectContent>
      </Select>
    </div>
  )
}
